import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import { Button, Form, Row, Col, Card } from "react-bootstrap";
import {
  fetchSubjects,
  deleteSubject,
  clearError,
  clearSuccess,
} from "../../redux/slices/subjectSlice";
import collegeService from "../../services/collegeService";
import CollegeSubjectModal from "./CollegeSubjectModal";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import DynamicBadge from "../Common/DynamicBadge";
import { FaPencilAlt, FaRegTrashAlt, FaUniversity, FaBook } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const CollegeSubjects = () => {
  const dispatch = useDispatch();
  const { subjects = [], isLoading, error, success } = useSelector((state) => state.subjects);
  
  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState(null);
  
  // Filters
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  
  const [universityFilter, setUniversityFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [semesterFilter, setSemesterFilter] = useState("");

  useEffect(() => {
    dispatch(fetchSubjects());
    
    // Load universities for filter
    const loadUniversities = async () => {
      try {
        const univData = await collegeService.getUniversities();
        setUniversities(univData);
      } catch (err) {
        console.error("Error loading universities:", err);
      }
    };
    loadUniversities();
  }, [dispatch]);

  useEffect(() => {
    if (universityFilter) {
      const loadCourses = async () => {
        try {
          const courseData = await collegeService.getCourses(universityFilter);
          setCourses(courseData);
          setCourseFilter("");
          setSemesterFilter("");
          setSemesters([]);
        } catch (err) {
          console.error("Error loading courses:", err);
        }
      };
      loadCourses();
    } else {
      setCourses([]);
      setCourseFilter("");
      setSemesterFilter("");
      setSemesters([]);
    }
  }, [universityFilter]);

  useEffect(() => {
    if (courseFilter) {
      const loadSemesters = async () => {
        try {
          const semData = await collegeService.getSemesters(courseFilter);
          setSemesters(semData);
          setSemesterFilter("");
        } catch (err) {
          console.error("Error loading semesters:", err);
        }
      };
      loadSemesters();
    } else {
      setSemesters([]);
      setSemesterFilter("");
    }
  }, [courseFilter]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setShowModal(true);
  };

  const handleDeleteClick = (subject) => {
    setSubjectToDelete(subject);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (subjectToDelete) {
      dispatch(deleteSubject(subjectToDelete.id));
      setShowConfirmModal(false);
      setSubjectToDelete(null);
    }
  };

  // Filter logic for subjects (only show subjects with semester_id)
  const collegeOnlySubjects = subjects.filter(s => s.semester_id);
  
  const filteredSubjects = collegeOnlySubjects.filter((s) => {
    let matches = true;
    if (universityFilter && s.university_id !== universityFilter) matches = false;
    if (courseFilter && s.course_id !== courseFilter) matches = false;
    if (semesterFilter && s.semester_id !== semesterFilter) matches = false;
    return matches;
  });

  const header = [
    {
      prop: "subject_name",
      title: "Subject Name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <span className="fw-bold text-primary">{row.subject_name}</span>
      )
    },
    {
      prop: "university_name",
      title: "University",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={row.university_name || "N/A"} color="#0ea5e9" />
      ),
    },
    {
      prop: "course_name",
      title: "Course",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={row.course_name || "N/A"} color="#06b6d4" />
      ),
    },
    {
      prop: "semester_name",
      title: "Semester",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={row.semester_name || "N/A"} color="#14b8a6" />
      ),
    },
    {
      prop: "createddate",
      title: "Created Date",
      isSortable: true,
      isFilterable: true,
      cell: (row) => formatDateToDDMMYYYY(row.createddate) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button variant="outline-dark" size="sm" onClick={() => handleEdit(row)}>
            <FaPencilAlt />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(row)}>
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          College Subjects
        </Link>
      </div>

      <Card className="shadow-sm border-0 mb-4 bg-light">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-bold text-muted small mb-1 uppercase">University</Form.Label>
              <Form.Select
                value={universityFilter}
                onChange={(e) => setUniversityFilter(e.target.value)}
                className="form-select-sm"
              >
                <option value="">All Universities</option>
                {universities.map((u) => (
                  <option key={u.id} value={u.id}>{u.university_name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-bold text-muted small mb-1 uppercase">Course</Form.Label>
              <Form.Select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="form-select-sm"
                disabled={!universityFilter}
              >
                <option value="">All Courses</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.course_name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-bold text-muted small mb-1 uppercase">Semester</Form.Label>
              <Form.Select
                value={semesterFilter}
                onChange={(e) => setSemesterFilter(e.target.value)}
                className="form-select-sm"
                disabled={!courseFilter}
              >
                <option value="">All Semesters</option>
                {semesters.map((s) => (
                  <option key={s.id} value={s.id}>{s.semester_name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <Button
                variant="dark"
                className="btn-sm px-3"
                onClick={() => {
                  setEditingSubject(null);
                  setShowModal(true);
                }}
              >
                <FaBook className="me-2" /> New Subject
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <DatatableWrapper
        body={filteredSubjects}
        headers={header}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 10, options: [5, 10, 20, 50] }
        }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "250px" }}>
              <Filter placeholder="Search subjects..." className="w-100" />
            </div>
            <PaginationOptions />
            <InfoPill
              left="Total Subjects"
              right={filteredSubjects?.length}
            />
          </div>
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-hover table-striped table-bordered align-middle">
            <TableHeader className="bg-light" />
            <TableBody />
          </table>
        </div>

        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      <CollegeSubjectModal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        subject={editingSubject} 
      />

      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Subject"
        message={`Are you sure you want to delete the subject "${subjectToDelete?.subject_name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={isLoading}
      />
    </div>
  );
};

export default CollegeSubjects;
